import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { DataListCardField } from "@/components/shared/DataListCardField";
import { DataListCardRow } from "@/components/shared/DataListCardRow";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { ResponsiveListSplit } from "@/components/shared/ResponsiveListSplit";
import { TablePagination } from "@/components/shared/TablePagination";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableActionsMenu } from "@/components/shared/TableActionsMenu";
import { TableCard } from "@/components/shared/TableCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate, isPastCalendarDay } from "@/lib/dateFormat";
import { cancelMyNotarialRequest } from "@/services/userNotarialRequestsService";
import {
  cancelMyAccountantRequest,
  listMergedMyCertificateRequests,
  type MergedConsumerRequestSummary,
} from "@/services/userAccountantRequestsService";
import {
  cancelMyQuoteRequest,
  extendMyQuoteRequest,
} from "@/services/quoteRequestsService";
import {
  getConsumerStatusBadgeVariant,
  getConsumerStatusTranslationKey,
} from "@/utils/consumerNotarialRequestView";
import {
  isQuoteRequestCancellable,
  isQuotingClosed,
  getConsumerStatusBadgeVariant as getQuoteConsumerStatusBadgeVariant,
  getConsumerStatusTranslationKey as getQuoteConsumerStatusTranslationKey,
} from "@/utils/consumerQuoteRequestView";
import { serverMessage } from "@/lib/apiError";
import {
  formatCertificatePricingProduct,
  formatCertificatePricingProductWithSource,
} from "@/utils/certificatePricingDisplay";
import { listResumableCheckouts } from "@/services/certificatePaymentsService";
import { ArrowRight, CalendarPlus, Eye, Info, Plus, Trash2 } from "lucide-react";
import {
  listMyEnterpriseRequests,
  type EnterpriseRequest,
} from "@/services/enterpriseCatalogService";
import { UserAvatar } from "@/components/ui/user-avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { StatusBadgeVariant } from "@/components/shared/StatusBadge";

/** Wraps consumer (notarial/accountant) and enterprise-request rows in one shape so both can render in the same table. */
type UnifiedRequestRow =
  | (MergedConsumerRequestSummary & { raw: null })
  | {
      source: "enterprise";
      id: string;
      status: string;
      requestType: string;
      createdAt: string;
      updatedAt: string;
      acceptedAt: string | null;
      estimatedCompletionDate: string | null;
      certificateValidityDate: string | null;
      raw: EnterpriseRequest;
    };

const ENTERPRISE_STATUS_VARIANT: Record<string, StatusBadgeVariant> = {
  pending: "warning",
  in_review: "info",
  needs_info: "danger",
  approved: "success",
  rejected: "softDanger",
  cancelled: "softDanger",
  completed: "success",
};

/**
 * Unanswered questions waiting on the consumer, or 0.
 *
 * Not derivable from the status: an RFQ with a professional's question
 * pending still reads "recibiendo cotizaciones", so the one row in the list
 * that needs the consumer to do something looks exactly like the ones that
 * do not. The catalog flow has no equivalent gap — `missing_information` is
 * already its own status and already renders as a danger badge here.
 */
function unansweredQuestionsOf(request: UnifiedRequestRow): number {
  return request.source === "quote" ? (request.unansweredQuestionCount ?? 0) : 0;
}

/** Live bids waiting to be reviewed, or 0. Same blind spot as the questions above. */
function waitingQuotesOf(request: UnifiedRequestRow): number {
  return request.source === "quote" ? (request.submittedQuoteCount ?? 0) : 0;
}

/**
 * Whether the row's quick Cancel action should appear.
 *
 * RFQ rows and catalog rows use different status vocabularies. Testing the
 * catalog statuses against every row is the bug this replaces: no RFQ status is
 * ever 'pending' or 'cannot_complete', so Cancel silently never rendered on a
 * quote row even while the detail page offered it.
 */
function canCancelRequestRow(request: UnifiedRequestRow): boolean {
  if (request.source === "quote") {
    return isQuoteRequestCancellable(request.status);
  }
  return request.status === "pending" || request.status === "cannot_complete";
}

/**
 * Whether the row may be given another week.
 *
 * Only an RFQ whose window has actually lapsed. The server refuses anything
 * else, and an action that is always visible but usually refused teaches
 * people to distrust the menu.
 */
function canExtendRequestRow(request: UnifiedRequestRow): boolean {
  return (
    request.source === "quote" &&
    isQuotingClosed(
      request.status as Parameters<typeof isQuotingClosed>[0],
      request.legs ?? [],
    )
  );
}

function sourceRequestTitleOf(request: UnifiedRequestRow): string | null {
  return request.source === "notarial" || request.source === "accountant"
    ? request.sourceRequestTitle
    : null;
}

/** The RFQ this row was spawned from, when it was spawned from one. */
function sourceRequestIdOf(request: UnifiedRequestRow): string | null {
  return request.source === "notarial" || request.source === "accountant"
    ? request.sourceRequestId
    : null;
}

/** A row, plus the RFQ it should be rendered underneath. */
type GroupedRequestRow = UnifiedRequestRow & { nestedUnder?: string };

/**
 * Puts the trámites spawned from an RFQ directly under it, indented.
 *
 * A two-discipline RFQ produces two children, and all three used to arrive as
 * unrelated siblings sorted by date: the same job listed three times, the two
 * children carrying identical titles ("Solicitud a medida — <RFQ title>") with
 * nothing to say which was the escribano's and which the contador's.
 *
 * A single-leg RFQ does not hit this, because it hands off — the parent leaves
 * the list entirely and its one child takes its place. Only the multi-leg case
 * keeps the parent, precisely because there is no single child to hand off to.
 *
 * Still one flat array, so pagination is untouched; grouping is order plus a
 * marker. Applied per tab, so a completed child whose parent is still active
 * shows as a normal top-level row rather than vanishing.
 */
function groupChildrenUnderParents(rows: UnifiedRequestRow[]): GroupedRequestRow[] {
  const parentIds = new Set(rows.filter((r) => r.source === "quote").map((r) => r.id));
  const childrenByParent = new Map<string, UnifiedRequestRow[]>();
  for (const row of rows) {
    const parentId = sourceRequestIdOf(row);
    if (parentId != null && parentIds.has(parentId)) {
      const bucket = childrenByParent.get(parentId);
      if (bucket) bucket.push(row);
      else childrenByParent.set(parentId, [row]);
    }
  }

  const out: GroupedRequestRow[] = [];
  for (const row of rows) {
    const parentId = sourceRequestIdOf(row);
    // Placed under its parent below, not here.
    if (parentId != null && parentIds.has(parentId)) continue;
    out.push(row);
    if (row.source === "quote") {
      for (const child of childrenByParent.get(row.id) ?? []) {
        out.push({ ...child, nestedUnder: row.id });
      }
    }
  }
  return out;
}

/**
 * What a nested child row is called. Its own title is "Solicitud a medida —
 * <RFQ title>", which under the parent that already says that is noise twice
 * over; the discipline is the only thing distinguishing the two children.
 */
function nestedChildLabelKey(request: UnifiedRequestRow): string {
  return request.source === "accountant"
    ? "requests.user.quoteDiscipline_accountant"
    : "requests.user.quoteDiscipline_notary";
}

function enterpriseStatusLabelKey(status: string): string {
  return `requests.user.enterprise.status${status
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join("")}`;
}

export function UserRequestsPage(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const pagination = usePaginationFromUrl();
  const tabParam = searchParams.get("tab");
  const initialTab = tabParam === "completed" ? "completed" : "active";
  const [activeTab, setActiveTab] = useState<"active" | "completed">(initialTab);
  const [requestToCancel, setRequestToCancel] = useState<MergedConsumerRequestSummary | null>(null);
  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ["me", "certificate-requests"],
    queryFn: listMergedMyCertificateRequests,
    refetchOnWindowFocus: false,
  });
  const enterpriseQuery = useQuery({
    queryKey: ["me", "enterprise-requests"],
    queryFn: listMyEnterpriseRequests,
    refetchOnWindowFocus: false,
  });
  // Safety net for interrupted post-payment resumes: a paid checkout that never
  // became a request (session lost, tab closed, profile-gate hang). Surfaces a
  // one-tap "finish your paid request" banner so the user is never stranded
  // with money spent and no request — most relevant on the same device they
  // paid from, where the resume blob still lives in IndexedDB.
  const resumableQuery = useQuery({
    queryKey: ["me", "certificate-payments", "resumable"],
    queryFn: listResumableCheckouts,
    refetchOnWindowFocus: false,
  });
  const resumableCheckout = resumableQuery.data?.[0] ?? null;
  const extendMutation = useMutation({
    mutationFn: (id: string) => extendMyQuoteRequest(id),
    onSuccess: () => {
      toast.success(t("requests.user.extendSuccess"));
      // The row's status, its legs' windows and its quote list all moved
      // server-side; re-read rather than patching the cache by hand.
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
    },
    onError: (error: unknown) => {
      // The server's refusal is the only actionable part of it: "no lapsed
      // quoting window to extend" tells the consumer their request is still
      // open, which a generic "try again" would hide.
      toast.error(serverMessage(error) ?? t("requests.user.extendError"));
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (row: MergedConsumerRequestSummary) => {
      if (row.source === "accountant") {
        await cancelMyAccountantRequest(row.id);
        return;
      }
      if (row.source === "quote") {
        await cancelMyQuoteRequest(row.id);
        return;
      }
      await cancelMyNotarialRequest(row.id);
    },
    onSuccess: () => {
      toast.success(t("requests.user.cancelSuccess"));
      setRequestToCancel(null);
      void queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["me", "notarial-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["me", "accountant-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests"] });
    },
    onError: () => {
      toast.error(t("requests.user.cancelError"));
    },
  });

  useEffect(() => {
    if (isError) {
      toast.error(t("requests.user.error"));
    }
  }, [isError, t]);

  const enterpriseItems = useMemo<UnifiedRequestRow[]>(
    () =>
      (enterpriseQuery.data ?? []).map((req) => ({
        source: "enterprise" as const,
        id: req.id,
        status: req.status,
        requestType: req.product?.displayName ?? req.productSlug,
        createdAt: req.createdAt,
        updatedAt: req.updatedAt,
        acceptedAt: null,
        estimatedCompletionDate: null,
        certificateValidityDate: null,
        raw: req,
      })),
    [enterpriseQuery.data],
  );
  const allItems = useMemo<UnifiedRequestRow[]>(
    () =>
      [
        ...(data ?? []).map((r) => ({ ...r, raw: null })),
        ...enterpriseItems,
      ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [data, enterpriseItems],
  );
  const isCompletedStatus = (row: UnifiedRequestRow): boolean => {
    if (row.source === "enterprise") {
      return row.status === "approved" || row.status === "rejected" || row.status === "cancelled";
    }
    return (
      row.status === "completed" ||
      row.status === "cannot_complete" ||
      row.status === "cancelled" ||
      row.status === "expired"
    );
  };
  const activeItems = useMemo(
    () => groupChildrenUnderParents(allItems.filter((r) => !isCompletedStatus(r))),
    [allItems],
  );
  const completedItems = useMemo(
    () => groupChildrenUnderParents(allItems.filter((r) => isCompletedStatus(r))),
    [allItems],
  );

  useEffect(() => {
    if (data != null && activeItems.length === 0 && completedItems.length > 0) {
      setActiveTab("completed");
    }
  }, [data, activeItems.length, completedItems.length]);

  const tabItems = activeTab === "active" ? activeItems : completedItems;

  useEffect(() => {
    if (data != null) {
      pagination.setTotal(tabItems.length);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [data, isError, tabItems.length, pagination]);

  useEffect(() => {
    if (data == null) return;
    const total = tabItems.length;
    const limit = pagination.limit;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    if (pagination.page > totalPages) {
      pagination.goToPage(totalPages);
    }
  }, [data, tabItems.length, pagination.page, pagination.limit, pagination.goToPage]);

  const pageItems = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return tabItems.slice(start, start + pagination.limit);
  }, [tabItems, pagination.page, pagination.limit]);

  const isAccountantPendingReview =
    (user?.role === "accountant" || user?.isAccountant === true) &&
    user.accountantActivationStatus === "pending";

  if (isLoading) {
    return (
      <div className="p-4 md:p-8">
        <div className="flex min-h-[40vh] items-center justify-center">
          <LoadingLogo size="lg" />
        </div>
      </div>
    );
  }

  const newRequestButton = (
    <Button
      type="button"
      onClick={() => navigate("/requests/new")}
      className="transition-colors duration-150"
    >
      <Plus className="mr-2 h-4 w-4" />
      {t("requests.user.newRequest")}
    </Button>
  );

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={t("navigation.requests")}
        description={t("requests.user.subtitle")}
        actions={newRequestButton}
      />
      {resumableCheckout ? (
        <Alert
          variant="warning"
          className="mt-4 transition-opacity duration-150 motion-reduce:transition-none"
        >
          <Info aria-hidden />
          <AlertTitle>
            {t("requests.user.resumePayment.title", "Tenés un pago confirmado sin completar")}
          </AlertTitle>
          <AlertDescription>
            <p>
              {t("requests.user.resumePayment.description", {
                defaultValue:
                  "Ya pagaste tu {{product}} pero la solicitud no se llegó a enviar. Continuá desde el mismo dispositivo para terminar — no vas a tener que volver a cargar tus documentos ni pagar otra vez.",
                product: formatCertificatePricingProduct(resumableCheckout.targetType, t),
              })}
            </p>
            <Button
              type="button"
              size="sm"
              className="mt-3"
              onClick={() =>
                navigate(
                  `/requests/new?certificate_payment=done&checkout_id=${encodeURIComponent(
                    resumableCheckout.checkoutId,
                  )}`,
                )
              }
            >
              {t("requests.user.resumePayment.cta", "Continuar solicitud")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}
      {isAccountantPendingReview ? (
        <Alert
          variant="info"
          className="mt-4 transition-opacity duration-150 motion-reduce:transition-none"
        >
          <Info className="text-blue-600 dark:text-blue-400" aria-hidden />
          <AlertTitle>{t("requests.user.accountantPendingReviewTitle")}</AlertTitle>
          <AlertDescription className="text-blue-900/90 dark:text-blue-100/90">
            {t("requests.user.accountantPendingReviewDescription")}
          </AlertDescription>
        </Alert>
      ) : null}
      <TableCard className="mt-4">
        {allItems.length === 0 ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <EmptyState
              title={t("requests.user.emptyTitle")}
              message={t("requests.user.emptyMessage")}
              action={
                <Button
                  type="button"
                  onClick={() => navigate("/requests/new")}
                  className="transition-colors duration-150"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t("requests.user.newRequest")}
                </Button>
              }
            />
          </div>
        ) : (
          <>
            <div
              role="tablist"
              aria-label={t("navigation.requests")}
              className="flex items-center gap-1 border-b border-border px-2 md:px-4"
            >
              {(
                [
                  { id: "active" as const, label: t("requests.user.tabActive"), count: activeItems.length },
                  { id: "completed" as const, label: t("requests.user.tabCompleted"), count: completedItems.length },
                ] as const
              ).map((tab) => {
                const isSelected = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    onClick={() => {
                      if (isSelected) return;
                      setActiveTab(tab.id);
                      pagination.goToPage(1);
                    }}
                    className={cn(
                      "relative inline-flex items-center gap-2 px-3 py-3 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none",
                      isSelected
                        ? "text-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <span>{tab.label}</span>
                    {"count" in tab && tab.count !== undefined && (
                      <span
                        className={cn(
                          "inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums transition-colors duration-150",
                          isSelected
                            ? "bg-primary/15 text-primary"
                            : "bg-muted text-muted-foreground",
                        )}
                      >
                        {tab.count}
                      </span>
                    )}
                    <span
                      aria-hidden
                      className={cn(
                        "absolute inset-x-2 -bottom-px h-0.5 rounded-full transition-colors duration-150",
                        isSelected ? "bg-primary" : "bg-transparent",
                      )}
                    />
                  </button>
                );
              })}
            </div>
            {tabItems.length === 0 ? (
              <div className="flex min-h-[30vh] items-center justify-center px-4 py-8 text-center text-sm text-muted-foreground">
                {activeTab === "active"
                  ? t("requests.user.tabEmptyActive")
                  : t("requests.user.tabEmptyCompleted")}
              </div>
            ) : (
              <>
            <ResponsiveListSplit
              table={
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("requests.user.requestTypeColumn")}</TableHead>
                      <TableHead>{t("notary.requests.status")}</TableHead>
                      <TableHead>{t("notary.requests.createdAt")}</TableHead>
                      <TableHead>{t("requests.user.estimatedCompletion")}</TableHead>
                      <TableHead>{t("requests.user.validityColumnHeader")}</TableHead>
                      <TableHead className="w-[80px]">{t("notary.requests.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageItems.map((request) => {
                    const isEnterprise = request.source === "enterprise";
                    const isQuote = request.source === "quote";
                    const consumerStatusLabelKey = isEnterprise
                      ? enterpriseStatusLabelKey(request.status)
                      : isQuote
                        ? getQuoteConsumerStatusTranslationKey(
                            request.status as Parameters<typeof getQuoteConsumerStatusTranslationKey>[0],
                            // THE LEGS ARE THE POINT. Without them the helper
                            // defaults to [] and answers "still open" for every
                            // row — which is how a request whose window shut
                            // two days earlier kept reading "Recibiendo
                            // cotizaciones". Both views of this page pass them.
                            request.legs ?? [],
                          )
                        : getConsumerStatusTranslationKey(request.status as Parameters<typeof getConsumerStatusTranslationKey>[0]);
                    const consumerStatusVariant: StatusBadgeVariant = isEnterprise
                      ? ENTERPRISE_STATUS_VARIANT[request.status] ?? "warning"
                      : isQuote
                        ? getQuoteConsumerStatusBadgeVariant(
                            request.status as Parameters<typeof getQuoteConsumerStatusBadgeVariant>[0],
                            // Same legs as the label above. Without them the
                            // chip stays calm blue while its own text says the
                            // window shut, which is worse than either alone.
                            request.legs ?? [],
                          )
                        : getConsumerStatusBadgeVariant(request.status as Parameters<typeof getConsumerStatusBadgeVariant>[0]);
                    const pendingQuestions = unansweredQuestionsOf(request);
                  const waitingQuotes = waitingQuotesOf(request);
                  // A child under its parent is named by its discipline; on
                  // its own it keeps the full "Solicitud a medida — …" title.
                  const isNested = request.nestedUnder != null;
                  const rowTitle = isNested
                    ? t(nestedChildLabelKey(request))
                    : request.source === "quote"
                      ? request.title
                      : formatCertificatePricingProductWithSource(
                          request.requestType,
                          sourceRequestTitleOf(request),
                          t,
                        );
                    return (
                      <TableRow
                        key={request.id}
                        // A tint, not just an indent. The rule and the inset
                        // say "belongs to the row above"; the background is
                        // what makes the group readable at a glance in a list
                        // of a dozen unrelated requests.
                        className={
                          isNested
                            ? "cursor-pointer bg-muted/40 transition-colors duration-150"
                            : "cursor-pointer transition-colors duration-150"
                        }
                        onClick={() =>
                          navigate(`/requests/${request.id}?source=${request.source}`)
                        }
                      >
                        <TableCell
                          className={
                            isNested
                              ? "text-muted-foreground"
                              : "font-medium text-foreground"
                          }
                        >
                          <div className="flex items-center gap-2">
                            {/* The rule is the nesting: a child sits inside
                                its parent's job, and an indent alone reads as
                                a stray margin rather than a relationship. */}
                            {isNested ? (
                              // A real indent plus a rule. An indent alone
                              // reads as a stray margin; the rule says the
                              // row belongs to the one above it.
                              <span
                                className="ml-4 mr-2 self-stretch border-l-2 border-border"
                                aria-hidden
                              />
                            ) : null}
                            <span>{rowTitle}</span>
                            {request.source === "enterprise" && request.raw && user && request.raw.userId !== user.id && (
                              <Badge variant="secondary" className="text-[10px] font-normal normal-case">
                                {t("requests.user.participantBadge")}
                              </Badge>
                            )}
                            {request.source === "enterprise" && request.raw && (() => {
                              // Build list of other people involved (exclude current user)
                              const isParticipant = request.raw!.userId !== user?.id;
                              const others: Array<{ id: string; firstName?: string | null; lastName?: string | null; email: string }> = [];
                              // If current user is a participant, show the initiator
                              if (isParticipant && request.raw!.user) {
                                others.push(request.raw!.user);
                              }
                              // Add other participants (exclude current user)
                              for (const p of request.raw!.participants ?? []) {
                                if (p.user && p.user.id !== user?.id) {
                                  others.push(p.user);
                                }
                              }
                              if (others.length === 0) return null;
                              return (
                                <TooltipProvider delayDuration={200}>
                                  <div className="flex -space-x-1.5">
                                    {others.map((o) => {
                                      const name = [o.firstName, o.lastName].filter(Boolean).join(" ").trim() || o.email;
                                      return (
                                        <Tooltip key={o.id}>
                                          <TooltipTrigger asChild>
                                            <span>
                                              <UserAvatar
                                                firstName={o.firstName ?? undefined}
                                                lastName={o.lastName ?? undefined}
                                                email={o.email}
                                                size="xs"
                                              />
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent side="top" className="text-xs">
                                            {name}
                                          </TooltipContent>
                                        </Tooltip>
                                      );
                                    })}
                                  </div>
                                </TooltipProvider>
                              );
                            })()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {/*
                              A payment-held row links back to its transfer
                              page, which re-runs request creation from the
                              stored receipt — so the badge is the way back
                              into a flow that stalled. Every other status is
                              not actionable from here and stays a plain badge.
                            */}
                            {request.status === "pending_payment_verification" &&
                            "certificateCheckoutId" in request &&
                            typeof request.certificateCheckoutId === "string" ? (
                              <Link
                                to={`/pay/bank-transfer?checkout_id=${encodeURIComponent(
                                  request.certificateCheckoutId,
                                )}`}
                                title={t("requests.user.paymentVerificationHint")}
                                className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary/40"
                              >
                                <StatusBadge variant={consumerStatusVariant}>
                                  {t(consumerStatusLabelKey)}
                                </StatusBadge>
                              </Link>
                            ) : (
                              <StatusBadge variant={consumerStatusVariant}>
                                {t(consumerStatusLabelKey)}
                              </StatusBadge>
                            )}
                            {/*
                              AWAITING PAYMENT GETS ITS OWN BADGE rather than
                              a different variant on the status one.

                              'awarded' renders "Cotización aceptada,
                              esperando pago" in the same info blue as
                              quoting, triaged and fulfilling — so the one
                              state where NOTHING moves until the consumer
                              pays looked like another thing being worked on.

                              Recolouring the status badge is worse:
                              "Cotización aceptada" is a true, finished fact
                              and amber would make the acceptance itself look
                              unresolved. The fact and the obligation are two
                              statements, so they get two badges — the idiom
                              this row already uses for pending questions.

                              Matches apps/mobile-users, which took the same
                              decision on the same screen.
                            */}
                            {isQuote && request.status === "awarded" ? (
                              <StatusBadge variant="warning">
                                {t("requests.user.awaitingPaymentBadge")}
                              </StatusBadge>
                            ) : null}
                            {pendingQuestions > 0 ? (
                              <StatusBadge variant="warning">
                                {t("requests.user.pendingQuestionsBadge", {
                                  count: pendingQuestions,
                                })}
                              </StatusBadge>
                            ) : null}
                            {waitingQuotes > 0 ? (
                              <StatusBadge variant="opportunity">
                                {t("requests.user.waitingQuotesBadge", {
                                  count: waitingQuotes,
                                })}
                              </StatusBadge>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>{formatDisplayDate(request.createdAt)}</TableCell>
                        <TableCell>
                          {/* The date no longer requires `acceptedAt`. An RFQ
                              has no single accepting professional, so it
                              leaves that null honestly — and the cell used to
                              require it, which meant a paid RFQ whose
                              delivery date was known and already shown on its
                              detail page still read "—" here. The progress
                              BAR still needs a start, so it appears only when
                              there is one. */}
                          {request.estimatedCompletionDate &&
                            request.status !== "completed" && request.status !== "cannot_complete" ? (() => {
                              const estimatedMs = new Date(request.estimatedCompletionDate).getTime();
                              const nowMs = Date.now();
                              // Overdue only once the DAY has passed, not from midnight on it.
                              const isOverdue = isPastCalendarDay(request.estimatedCompletionDate);
                              const acceptedMs = request.acceptedAt ? new Date(request.acceptedAt).getTime() : null;
                              const totalDuration = acceptedMs != null ? estimatedMs - acceptedMs : null;
                              const progress =
                                acceptedMs != null && totalDuration != null
                                  ? totalDuration > 0
                                    ? Math.min(100, Math.max(0, ((nowMs - acceptedMs) / totalDuration) * 100))
                                    : 100
                                  : null;
                              const barColor = isOverdue ? "bg-red-500" : (progress ?? 0) >= 80 ? "bg-amber-400" : "bg-primary";
                              return (
                                <div className="min-w-[120px] space-y-1">
                                  <span className="block text-xs text-muted-foreground">
                                    {formatDisplayDate(request.estimatedCompletionDate)}
                                  </span>
                                  {progress != null ? (
                                    <div className="h-1.5 w-full rounded-full bg-muted dark:bg-white/10">
                                      <div className={`h-1.5 rounded-full ${barColor}`} style={{ width: `${Math.min(progress, 100)}%` }} />
                                    </div>
                                  ) : null}
                                  {isOverdue ? (
                                    <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-600 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-400">
                                      {t("requests.user.progressOverdue")}
                                    </span>
                                  ) : null}
                                </div>
                              );
                            })() : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                        </TableCell>
                        <TableCell>
                          {request.certificateValidityDate ? (() => {
                            const validityMs = new Date(request.certificateValidityDate).getTime();
                            const nowMs = Date.now();
                            const daysLeft = Math.ceil((validityMs - nowMs) / (1000 * 60 * 60 * 24));
                            const isExpired = daysLeft < 0;
                            const isExpiringSoon = !isExpired && daysLeft <= 30;
                            const badgeVariant = isExpired ? "danger" : isExpiringSoon ? "warning" : "success";
                            const badgeLabel = isExpired
                              ? t("requests.user.validityExpired")
                              : isExpiringSoon
                                ? t("requests.user.validityExpiringSoon")
                                : t("requests.user.validityValid");
                            return (
                              <div className="space-y-0.5">
                                <span className="text-xs text-muted-foreground">{formatDisplayDate(request.certificateValidityDate)}</span>
                                <StatusBadge variant={badgeVariant}>{badgeLabel}</StatusBadge>
                              </div>
                            );
                          })() : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <TableActionsMenu
                            actions={[
                              {
                                label: t("notary.requests.view"),
                                icon: Eye,
                                onClick: () =>
                                  navigate(`/requests/${request.id}?source=${request.source}`),
                              },
                              ...(canExtendRequestRow(request)
                                ? [
                                    {
                                      label: t("requests.user.extendRequest"),
                                      icon: CalendarPlus,
                                      onClick: () => extendMutation.mutate(request.id),
                                    },
                                  ]
                                : []),
                              ...(!isEnterprise && canCancelRequestRow(request)
                                ? [
                                    {
                                      label: t("requests.user.cancelRequest"),
                                      icon: Trash2,
                                      onClick: () => setRequestToCancel(request as MergedConsumerRequestSummary),
                                      variant: "destructive" as const,
                                    },
                                  ]
                                : []),
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  </TableBody>
                </Table>
              }
              cards={
                <div className="divide-y divide-border">
                  {pageItems.map((request) => {
                  const isEnterprise = request.source === "enterprise";
                  const isQuote = request.source === "quote";
                  const consumerStatusLabelKey = isEnterprise
                    ? enterpriseStatusLabelKey(request.status)
                    : isQuote
                      ? getQuoteConsumerStatusTranslationKey(
                            request.status as Parameters<typeof getQuoteConsumerStatusTranslationKey>[0],
                            // THE LEGS ARE THE POINT. Without them the helper
                            // defaults to [] and answers "still open" for every
                            // row — which is how a request whose window shut
                            // two days earlier kept reading "Recibiendo
                            // cotizaciones". Both views of this page pass them.
                            request.legs ?? [],
                          )
                      : getConsumerStatusTranslationKey(request.status as Parameters<typeof getConsumerStatusTranslationKey>[0]);
                  const consumerStatusVariant: StatusBadgeVariant = isEnterprise
                    ? ENTERPRISE_STATUS_VARIANT[request.status] ?? "warning"
                    : isQuote
                      ? getQuoteConsumerStatusBadgeVariant(
                            request.status as Parameters<typeof getQuoteConsumerStatusBadgeVariant>[0],
                            // Same legs as the label above. Without them the
                            // chip stays calm blue while its own text says the
                            // window shut, which is worse than either alone.
                            request.legs ?? [],
                          )
                      : getConsumerStatusBadgeVariant(request.status as Parameters<typeof getConsumerStatusBadgeVariant>[0]);
                  const pendingQuestions = unansweredQuestionsOf(request);
                  const waitingQuotes = waitingQuotesOf(request);
                  // A child under its parent is named by its discipline; on
                  // its own it keeps the full "Solicitud a medida — …" title.
                  const isNested = request.nestedUnder != null;
                  const rowTitle = isNested
                    ? t(nestedChildLabelKey(request))
                    : request.source === "quote"
                      ? request.title
                      : formatCertificatePricingProductWithSource(
                          request.requestType,
                          sourceRequestTitleOf(request),
                          t,
                        );
                  return (
                    <DataListCardRow
                      key={request.id}
                      className={isNested ? "bg-muted/40" : undefined}
                      onClick={() =>
                        navigate(`/requests/${request.id}?source=${request.source}`)
                      }
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p
                          className={
                            isNested
                              ? "ml-4 min-w-0 flex-1 border-l-2 border-border pl-3 text-sm leading-snug text-muted-foreground"
                              : "min-w-0 flex-1 font-semibold leading-snug text-foreground"
                          }
                        >
                          {rowTitle}
                        </p>
                        <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                          <TableActionsMenu
                            actions={[
                              {
                                label: t("notary.requests.view"),
                                icon: Eye,
                                onClick: () =>
                                  navigate(`/requests/${request.id}?source=${request.source}`),
                              },
                              ...(canExtendRequestRow(request)
                                ? [
                                    {
                                      label: t("requests.user.extendRequest"),
                                      icon: CalendarPlus,
                                      onClick: () => extendMutation.mutate(request.id),
                                    },
                                  ]
                                : []),
                              ...(!isEnterprise && canCancelRequestRow(request)
                                ? [
                                    {
                                      label: t("requests.user.cancelRequest"),
                                      icon: Trash2,
                                      onClick: () => setRequestToCancel(request as MergedConsumerRequestSummary),
                                      variant: "destructive" as const,
                                    },
                                  ]
                                : []),
                            ]}
                          />
                        </div>
                      </div>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <DataListCardField label={t("notary.requests.status")}>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {/*
                              A payment-held row links back to its transfer
                              page, which re-runs request creation from the
                              stored receipt — so the badge is the way back
                              into a flow that stalled. Every other status is
                              not actionable from here and stays a plain badge.
                            */}
                            {request.status === "pending_payment_verification" &&
                            "certificateCheckoutId" in request &&
                            typeof request.certificateCheckoutId === "string" ? (
                              <Link
                                to={`/pay/bank-transfer?checkout_id=${encodeURIComponent(
                                  request.certificateCheckoutId,
                                )}`}
                                title={t("requests.user.paymentVerificationHint")}
                                className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary/40"
                              >
                                <StatusBadge variant={consumerStatusVariant}>
                                  {t(consumerStatusLabelKey)}
                                </StatusBadge>
                              </Link>
                            ) : (
                              <StatusBadge variant={consumerStatusVariant}>
                                {t(consumerStatusLabelKey)}
                              </StatusBadge>
                            )}
                            {pendingQuestions > 0 ? (
                              <StatusBadge variant="warning">
                                {t("requests.user.pendingQuestionsBadge", {
                                  count: pendingQuestions,
                                })}
                              </StatusBadge>
                            ) : null}
                            {waitingQuotes > 0 ? (
                              <StatusBadge variant="opportunity">
                                {t("requests.user.waitingQuotesBadge", {
                                  count: waitingQuotes,
                                })}
                              </StatusBadge>
                            ) : null}
                          </div>
                        </DataListCardField>
                        <DataListCardField label={t("notary.requests.createdAt")}>
                          {formatDisplayDate(request.createdAt)}
                        </DataListCardField>
                        {request.estimatedCompletionDate && request.acceptedAt &&
                          request.status !== "completed" && request.status !== "cannot_complete" ? (
                          <DataListCardField label={t("requests.user.estimatedCompletion")}>
                            <div className="space-y-1">
                              <span className="text-xs">{formatDisplayDate(request.estimatedCompletionDate)}</span>
                              {(() => {
                                const acceptedMs = new Date(request.acceptedAt).getTime();
                                const estimatedMs = new Date(request.estimatedCompletionDate).getTime();
                                const nowMs = Date.now();
                                const totalDuration = estimatedMs - acceptedMs;
                                const elapsed = nowMs - acceptedMs;
                                const progress = totalDuration > 0 ? Math.min(100, Math.max(0, (elapsed / totalDuration) * 100)) : 100;
                                // Overdue only once the DAY has passed, not from midnight on it.
                              const isOverdue = isPastCalendarDay(request.estimatedCompletionDate);
                                const barColor = isOverdue ? "bg-red-500" : progress >= 80 ? "bg-amber-400" : "bg-primary";
                                return (
                                  <>
                                    <div className="h-1.5 w-full rounded-full bg-muted dark:bg-white/10">
                                      <div className={`h-1.5 rounded-full ${barColor}`} style={{ width: `${Math.min(progress, 100)}%` }} />
                                    </div>
                                    {isOverdue ? (
                                    <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-600 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-400">
                                      {t("requests.user.progressOverdue")}
                                    </span>
                                  ) : null}
                                  </>
                                );
                              })()}
                            </div>
                          </DataListCardField>
                        ) : null}
                        {request.certificateValidityDate ? (() => {
                          const validityMs = new Date(request.certificateValidityDate).getTime();
                          const nowMs = Date.now();
                          const daysLeft = Math.ceil((validityMs - nowMs) / (1000 * 60 * 60 * 24));
                          const isExpired = daysLeft < 0;
                          const isExpiringSoon = !isExpired && daysLeft <= 30;
                          const badgeVariant = isExpired ? "danger" : isExpiringSoon ? "warning" : "success";
                          const badgeLabel = isExpired
                            ? t("requests.user.validityExpired")
                            : isExpiringSoon
                              ? t("requests.user.validityExpiringSoon")
                              : t("requests.user.validityValid");
                          return (
                            <DataListCardField label={t("requests.user.validityColumnHeader")}>
                              <div className="flex items-center gap-2">
                                <span className="text-xs">{formatDisplayDate(request.certificateValidityDate)}</span>
                                <StatusBadge variant={badgeVariant}>{badgeLabel}</StatusBadge>
                              </div>
                            </DataListCardField>
                          );
                        })() : null}
                      </div>
                    </DataListCardRow>
                  );
                  })}
                </div>
              }
            />
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("notary.requests.rowsPerPage")}
              pageOfLabel={t("notary.requests.pageOf", {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(pagination.page * pagination.limit, pagination.total),
                total: pagination.total,
              })}
              onRefresh={() => {
                void refetch();
              }}
              isRefreshing={isFetching}
            />
              </>
            )}
          </>
        )}
      </TableCard>
      <ConfirmationDialog
        open={requestToCancel != null}
        onOpenChange={(open) => {
          if (!open) setRequestToCancel(null);
        }}
        title={t("requests.user.cancelDialogTitle")}
        description={
          requestToCancel?.status === "cannot_complete"
            ? t("requests.user.cancelDialogDescriptionCannotComplete")
            : t("requests.user.cancelDialogDescription")
        }
        confirmText={t("requests.user.cancelRequest")}
        loadingText={t("requests.user.cancelLoading")}
        cancelText={t("pages.common.cancel")}
        variant="destructive"
        isLoading={cancelMutation.isPending}
        onConfirm={() => {
          if (!requestToCancel) return;
          return cancelMutation.mutateAsync(requestToCancel);
        }}
      />
    </div>
  );
}
